import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LayoutDashboard, ArrowDownToLine, ArrowUpFromLine, ReceiptIndianRupee, Settings2, RefreshCw, Sparkles, LogOut, Wand2, Hourglass, Bell, BookText, HandCoins, AlertTriangle, X, FolderKanban, Users, Receipt, Boxes, QrCode, Menu, Scale, Landmark, Warehouse, ShieldCheck, ChevronDown, ChevronRight, TrendingUp, Globe, Percent, Tag, FileOutput, ClipboardCheck, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { useAuth, API, isAdmin, canAccess } from "@/context/AuthContext";
import AIAgent from "@/components/AIAgent";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { inrShort } from "@/pages/Dashboard";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, context: "dashboard", page: "dashboard" },
  { to: "/ap-ageing", label: "AP Ageing", icon: ArrowUpFromLine, context: "ap", page: "ap-ageing", group: "finance" },
  { to: "/ar-ageing", label: "AR Ageing", icon: ArrowDownToLine, context: "ar", page: "ar-ageing", group: "finance" },
  { to: "/ap-ledger", label: "AP Ledger", icon: BookText, context: "ap", page: "ap-ledger", group: "finance" },
  { to: "/ar-ledger", label: "AR Ledger", icon: BookText, context: "ar", page: "ar-ledger", group: "finance" },
  { to: "/retention-tracker", label: "Retention Tracker", icon: Hourglass, context: "dashboard", page: "retention-tracker", group: "finance" },
  { to: "/project-pnl", label: "Project P&L", icon: FolderKanban, context: "dashboard", page: "project-pnl", group: "finance" },
  { to: "/advance-requests", label: "Advance Requests", icon: HandCoins, context: "dashboard", page: "advance-requests", group: "finance" },
  { to: "/purchase-reco", label: "Purchase Reco (Tally vs SAP)", icon: Scale, context: "dashboard", page: "purchase-reco", group: "finance" },
  { to: "/tally-export", label: "Tally Export (SAP → Tally)", icon: FileOutput, context: "dashboard", page: "tally-export", group: "finance" },
  { to: "/gstr2", label: "GSTR-2 Report", icon: ReceiptIndianRupee, context: "gstr2", page: "gstr2", group: "gst" },
  { to: "/gstr3b", label: "GSTR-3B Summary", icon: ReceiptIndianRupee, context: "dashboard", page: "gstr3b", group: "gst" },
  { to: "/tds-summary", label: "TDS Summary", icon: Receipt, context: "dashboard", page: "tds-summary", group: "gst" },
  { to: "/subcon-recon", label: "Subcon Challan Reco & ITC-04", icon: ClipboardCheck, context: "dashboard", page: "subcon-recon", group: "gst" },
  { to: "/gst-returns", label: "GST Returns (GSTR-1 / 3B)", icon: FileSpreadsheet, context: "dashboard", page: "gst-returns", group: "gst" },
  { to: "/inventory", label: "Stock Reco", icon: Boxes, context: "dashboard", page: "inventory", group: "inventory" },
  { to: "/stock-ageing", label: "Stock Ageing", icon: Hourglass, context: "dashboard", page: "stock-ageing", group: "inventory" },
  { to: "/barcode-printing", label: "Barcode Printing", icon: QrCode, context: "dashboard", page: "barcode-printing", group: "stores" },
  { to: "/sales/overview", label: "Overview", icon: TrendingUp, context: "dashboard", page: "sales-performance", group: "sales" },
  { to: "/sales/customers", label: "Customer-wise", icon: Users, context: "dashboard", page: "sales-performance", group: "sales" },
  { to: "/sales/projects", label: "Project-wise", icon: FolderKanban, context: "dashboard", page: "sales-performance", group: "sales" },
  { to: "/sales/products", label: "Product-wise", icon: Boxes, context: "dashboard", page: "sales-performance", group: "sales" },
  { to: "/sales/geography", label: "Geography Map", icon: Globe, context: "dashboard", page: "sales-performance", group: "sales" },
  { to: "/sales/margins", label: "Margin Analysis", icon: Percent, context: "dashboard", page: "sales-performance", group: "sales" },
  { to: "/sales/prices", label: "Price Realisation", icon: Tag, context: "dashboard", page: "sales-performance", group: "sales" },
  { to: "/sales/proforma", label: "Proforma Invoice", icon: FileOutput, context: "dashboard", page: "proforma-invoice", group: "sales" },
  { to: "/report-builder", label: "Report Builder", icon: Wand2, context: "dashboard", page: "report-builder" },
  { to: "/users", label: "User Management", icon: Users, context: "dashboard", adminOnly: true, group: "admin" },
  { to: "/settings", label: "SAP Settings", icon: Settings2, context: "dashboard", adminOnly: true, group: "admin" },
];
const GROUPS = [
  { key: "finance", label: "Finance", icon: Landmark },
  { key: "gst", label: "GST & TDS", icon: ReceiptIndianRupee },
  { key: "inventory", label: "Inventory", icon: Boxes },
  { key: "stores", label: "Stores", icon: Warehouse },
  { key: "sales", label: "Sales Performance", icon: TrendingUp },
  { key: "admin", label: "Admin", icon: ShieldCheck },
];
const GROUP_STATE_KEY = "nav.groups.open";

function NavItem({ item, pendingCount, nested }) {
  return (
    <NavLink
      to={item.to}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 ${nested ? "pl-9 pr-3 py-2" : "px-3 py-2.5"} rounded-md text-sm font-medium transition-colors duration-200 ${
          isActive ? "bg-sky-700 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"
        }`
      }
    >
      <item.icon className={nested ? "h-3.5 w-3.5" : "h-4 w-4"} />
      <span className="flex-1 truncate">{item.label}</span>
      {item.to === "/users" && pendingCount > 0 && (
        <span className="ml-auto h-5 min-w-[20px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="nav-pending-badge">
          {pendingCount}
        </span>
      )}
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrefill, setAiPrefill] = useState(null);
  useEffect(() => {
    const h = (e) => { setAiPrefill(e.detail?.question || null); setAiOpen(true); };
    window.addEventListener("open-ai-agent", h);
    return () => window.removeEventListener("open-ai-agent", h);
  }, []);
  const [syncing, setSyncing] = useState(false);
  const [syncStartedAt, setSyncStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [lastSync, setLastSync] = useState(null);
  const [status, setStatus] = useState("demo");
  const [alerts, setAlerts] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => { setNavOpen(false); }, [location.pathname]);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    axios.get(`${API}/sap/connection`).then((r) => {
      setLastSync(r.data.last_sync);
      setStatus(r.data.status || "demo");
    }).catch(() => {});
    axios.get(`${API}/sap/sync-status`).then((r) => {
      if (r.data.running) { setSyncing(true); setSyncStartedAt(r.data.started_at); pollSyncStatus(); }
    }).catch(() => {});
    const loadAlerts = () => axios.get(`${API}/alerts/overdue`).then((r) => setAlerts(r.data)).catch(() => {});
    loadAlerts();
    window.addEventListener("sap-synced", loadAlerts);
    return () => window.removeEventListener("sap-synced", loadAlerts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") { setPendingCount(0); return; }
    const loadPending = () => axios.get(`${API}/admin/pending-count`).then((r) => setPendingCount(r.data.count || 0)).catch(() => {});
    loadPending();
    window.addEventListener("users-changed", loadPending);
    const poll = setInterval(loadPending, 30000);
    return () => { window.removeEventListener("users-changed", loadPending); clearInterval(poll); };
  }, [user]);

  useEffect(() => {
    if (!syncing || !syncStartedAt) { setElapsed(0); return; }
    const start = new Date(syncStartedAt).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [syncing, syncStartedAt]);

  const context = (NAV.find((n) => n.to === location.pathname) || NAV[0]).context;
  const admin = isAdmin(user);
  const canSync = admin || !!user?.can_sync;
  const visibleNav = NAV.filter((item) => {
    if (item.adminOnly) return admin;
    if (!item.page) return true;
    return canAccess(user, item.page);
  });
  const activeGroup = NAV.find((n) => n.to === location.pathname)?.group;
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem(GROUP_STATE_KEY) || "null") || { finance: true }; } catch { return { finance: true }; }
  });
  useEffect(() => { if (activeGroup && !openGroups[activeGroup]) setOpenGroups((g) => ({ ...g, [activeGroup]: true })); }, [activeGroup]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleGroup = (k) => setOpenGroups((g) => { const n = { ...g, [k]: !g[k] }; localStorage.setItem(GROUP_STATE_KEY, JSON.stringify(n)); return n; });
  const menu = [];
  visibleNav.forEach((item) => {
    if (!item.group) { menu.push({ item }); return; }
    let grp = menu.find((m) => m.group?.key === item.group);
    if (!grp) { grp = { group: GROUPS.find((g) => g.key === item.group), items: [] }; menu.push(grp); }
    grp.items.push(item);
  });

  const pollSyncStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/sap/sync-status`);
      if (data.running) {
        // The running sync stamps a heartbeat every 20 s; "stale" = its server process stopped (restart / publish).
        // The server re-runs an interrupted sync on its own about 2 minutes after it comes back up.
        if (data.stale) {
          setSyncing(false);
          setSyncStartedAt(null);
          toast.warning("The last SAP sync was interrupted because the server restarted (for example during a publish). It restarts on its own within about 2 minutes — or click Sync to run it now.", { id: "sync-stalled", duration: 15000 });
          return;
        }
        setTimeout(pollSyncStatus, 4000);
        return;
      }
      setSyncing(false);
      setSyncStartedAt(null);
      const r = data.result || {};
      if (r.last_sync) setLastSync(r.last_sync);
      if (r.source === "sap_live") {
        setStatus("connected");
        const api = r.source_api === "journal_entry_items" ? "Journal Entry Items (open items)" : "Invoice APIs";
        toast.success(`Live SAP extraction via ${api} — ${r.ap_count} AP, ${r.ar_count} AR, ${r.gstr2_count} GSTR-2 documents`);
        (r.warnings || []).forEach((w) => toast.warning(w, { duration: 12000 }));
      } else if (r.status === "fallback_demo" || r.status === "error") {
        setStatus("error");
        setBannerDismissed(false);
        toast.warning(`SAP tenant unreachable — kept your last synced data (no demo data loaded).${r.error ? " " + String(r.error).slice(0, 160) : ""} Check SAP Settings for details.`, { duration: 12000 });
      } else if (r.status) {
        toast.success(`SAP extraction complete — ${r.ap_count} AP, ${r.ar_count} AR, ${r.gstr2_count} GSTR-2 documents`);
      }
      window.dispatchEvent(new Event("sap-synced"));
    } catch (e) {
      setSyncing(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/sap/sync`);
      setSyncStartedAt(data.started_at || new Date().toISOString());
      if (data.status === "running") {
        toast.info("A sync is already running — extracting SAP data in the background…");
      } else {
        toast.info("SAP extraction started in the background. Large live tenants can take a couple of minutes…", { duration: 8000 });
      }
      pollSyncStatus();
    } catch (e) {
      setSyncing(false);
      toast.error("Could not start sync");
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      {navOpen && <button type="button" aria-label="Close menu" onClick={() => setNavOpen(false)} className="fixed inset-0 z-40 bg-slate-900/60 lg:hidden" data-testid="mobile-nav-backdrop" />}
      <aside className={`w-60 fixed inset-y-0 left-0 bg-slate-900 flex flex-col z-50 transition-transform duration-200 ${navOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`} data-testid="sidebar">
        <div className="px-5 py-6 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-md bg-white flex items-center justify-center overflow-hidden shrink-0">
              <img src="/yd-logo.png" alt="Yogiji Digi Ltd" className="h-9 w-9 object-contain" data-testid="client-logo" />
            </div>
            <div>
              <p className="text-white font-bold text-sm leading-tight" style={{ fontFamily: "Manrope" }}>Yogiji Digi</p>
              <p className="text-slate-400 text-[10px] uppercase tracking-widest">Analytics Platform</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
          {menu.map((entry) => entry.item ? (
            <NavItem key={entry.item.to} item={entry.item} pendingCount={pendingCount} />
          ) : (
            <div key={entry.group.key} data-testid={`nav-group-${entry.group.key}`}>
              <button
                type="button"
                onClick={() => toggleGroup(entry.group.key)}
                aria-expanded={!!openGroups[entry.group.key]}
                data-testid={`nav-group-toggle-${entry.group.key}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors duration-200 ${
                  activeGroup === entry.group.key && !openGroups[entry.group.key] ? "text-white bg-slate-800" : "text-slate-300 hover:text-white hover:bg-slate-800"
                }`}
              >
                <entry.group.icon className="h-4 w-4" />
                <span className="flex-1 text-left">{entry.group.label}</span>
                {entry.group.key === "admin" && pendingCount > 0 && !openGroups.admin && (
                  <span className="h-5 min-w-[20px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">{pendingCount}</span>
                )}
                {openGroups[entry.group.key] ? <ChevronDown className="h-3.5 w-3.5 text-slate-500" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-500" />}
              </button>
              {openGroups[entry.group.key] && (
                <div className="mt-0.5 space-y-0.5 border-l border-slate-800 ml-5">
                  {entry.items.map((item) => <NavItem key={item.to} item={item} pendingCount={pendingCount} nested />)}
                </div>
              )}
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800 shrink-0">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-white text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</p>
              <p className="text-slate-500 text-xs truncate" data-testid="current-user-email">{user?.email}</p>
              <span className={`inline-block mt-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${admin ? "bg-sky-700 text-white" : "bg-slate-700 text-slate-300"}`} data-testid="current-user-role">
                {admin ? "Admin" : "Report User"}
              </span>
            </div>
            <div className="flex items-center shrink-0">
              <ChangePasswordDialog />
              <Button variant="ghost" size="icon" data-testid="logout-button" onClick={logout} className="text-slate-400 hover:text-white hover:bg-slate-800 shrink-0">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex-1 lg:ml-60 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-3 sm:px-4 lg:px-8 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs text-slate-500 min-w-0">
            <Button variant="outline" size="icon" className="lg:hidden h-9 w-9 border-slate-300 shrink-0" onClick={() => setNavOpen(true)} aria-label="Open menu" data-testid="mobile-nav-toggle"><Menu className="h-4 w-4" /></Button>
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium ${status === "connected" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`} data-testid="sap-mode-badge">
              <span className={`h-1.5 w-1.5 rounded-full ${status === "connected" ? "bg-emerald-500" : "bg-amber-500"}`} />
              {status === "connected" ? "Live SAP Data" : "Demo SAP Data"}
            </span>
            {lastSync && <span data-testid="last-sync-label" className="hidden md:inline truncate">Last extraction: {new Date(lastSync).toLocaleString()}</span>}
            {syncing && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 font-medium text-sky-700" data-testid="sync-progress-pill">
                <RefreshCw className="h-3 w-3 animate-spin" />
                Extracting SAP data… {Math.floor(elapsed / 60)}m {elapsed % 60}s
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="icon" className="relative border-slate-300 h-9 w-9" data-testid="alerts-bell-button">
                  <Bell className="h-4 w-4 text-slate-600" />
                  {alerts?.count > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 h-5 min-w-[20px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="alerts-count-badge">
                      {alerts.count}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[420px] p-0" data-testid="alerts-panel">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-900 rounded-t-md">
                  <p className="text-white text-sm font-semibold">Overdue Alerts — beyond 90 days</p>
                  <p className="text-slate-400 text-xs mt-0.5">
                    AP {inrShort(alerts?.ap_total || 0)} · AR {inrShort(alerts?.ar_total || 0)} · {alerts?.count || 0} invoices
                  </p>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {(alerts?.alerts || []).length === 0 && <p className="text-sm text-slate-400 p-4 text-center">No invoices beyond 90 days overdue 🎉</p>}
                  {(alerts?.alerts || []).map((a, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 hover:bg-slate-50" data-testid={`alert-row-${i}`}>
                      <Badge variant="outline" className={`text-[10px] shrink-0 ${a.side === "AP" ? "border-sky-300 text-sky-700" : "border-amber-300 text-amber-700"}`}>{a.side}</Badge>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-slate-800 truncate">{a.party}</p>
                        <p className="text-xs text-slate-400 truncate">{a.invoice_no} · {a.wbs_element} · due {a.due_date}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold text-slate-900">{inrShort(a.amount)}</p>
                        <p className="text-xs font-medium text-red-600">{a.days_overdue}d overdue</p>
                      </div>
                    </div>
                  ))}
                  {alerts?.count > (alerts?.alerts || []).length && (
                    <p className="text-xs text-slate-400 p-3 text-center" data-testid="alerts-more-note">Showing top {alerts.alerts.length} of {alerts.count} by amount — see AP/AR Ageing for the full list</p>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="outline" size="sm" data-testid="sync-sap-button" onClick={sync} disabled={syncing || !canSync} className={`border-slate-300 ${canSync ? "" : "hidden"}`}>
              <RefreshCw className={`h-4 w-4 sm:mr-2 ${syncing ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">{syncing ? "Extracting..." : "Sync SAP Data"}</span>
            </Button>
            <Button size="sm" data-testid="open-ai-agent-button" onClick={() => setAiOpen(true)} className="bg-sky-700 hover:bg-sky-800 text-white active:scale-[0.98] transition-transform duration-200">
              <Sparkles className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">AI Analyst</span>
            </Button>
          </div>
        </header>
        {status === "error" && !bannerDismissed && (
          <div className="bg-amber-50 border-b border-amber-200 px-8 py-2.5 flex items-center justify-between gap-4" data-testid="sync-health-banner">
            <div className="flex items-center gap-2.5 text-sm text-amber-800 min-w-0">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
              <span className="truncate"><strong className="font-semibold">Last SAP sync failed</strong> — showing your previously synced data (not refreshed). No demo data was loaded.</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {canSync && (
                <Button variant="outline" size="sm" onClick={sync} disabled={syncing} className="h-7 border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="banner-retry-sync">
                  <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} />Retry Sync
                </Button>
              )}
              {admin && <NavLink to="/settings" className="text-xs font-medium text-amber-700 hover:text-amber-900 underline" data-testid="banner-settings-link">SAP Settings</NavLink>}
              <button onClick={() => setBannerDismissed(true)} className="text-amber-500 hover:text-amber-700" data-testid="banner-dismiss"><X className="h-4 w-4" /></button>
            </div>
          </div>
        )}
        <main className="flex-1 p-3 sm:p-5 lg:p-8">
          <Outlet />
        </main>
      </div>

      <AIAgent open={aiOpen} onOpenChange={setAiOpen} context={context} prefill={aiPrefill} />
    </div>
  );
}
